import { createSelector } from 'reselect';
import { getEntityByName } from '../appEntities';
import { get, isEqual } from 'lodash';
import {
    CREATE_USER_FAILURE,
    CREATE_USER_LOAD,
    CREATE_USER_SUCCESS,
    EDIT_USER_FAILURE,
    EDIT_USER_LOAD,
    EDIT_USER_SUCCESS,
    DELETE_USER_FAILURE,
    DELETE_USER_LOAD,
    DELETE_USER_SUCCESS,
    USERS_FAILURE,
    USERS_LOAD,
    USERS_SUCCESS,
    ROLES_LOAD,
    ROLES_SUCCESS,
    ROLES_FAILURE,
    CLEAR_CREATED_USER
} from './userManagementActions';

export default function reducer(state = {}, action = {}) {
    let newState;

    switch (action.type) {
        case CREATE_USER_FAILURE:
            newState = {
                ...state,
                createUserError: action.error,
                isCreateUserLoading: false
            };

            return isEqual(state, newState) ? state : newState;

        case CREATE_USER_LOAD:
            newState = {
                ...state,
                createUserError: null,
                isCreateUserLoading: true,
                isCreateUserSuccessful: false
            };

            return isEqual(state, newState) ? state : newState;

        case CREATE_USER_SUCCESS:
            newState = {
                ...state,
                createdUser: action.user,
                isCreateUserLoading: false,
                isCreateUserSuccessful: true
            };

            return isEqual(state, newState) ? state : newState;

        case CLEAR_CREATED_USER:
            newState = {
                ...state,
                createdUser: null
            };

            return isEqual(state, newState) ? state : newState;

        case EDIT_USER_FAILURE:
            newState = {
                ...state,
                editUserError: action.error,
                isEditUserLoading: false
            };

            return isEqual(state, newState) ? state : newState;

        case EDIT_USER_LOAD:
            newState = {
                ...state,
                editUserError: null,
                isEditUserLoading: true,
                isEditUserSuccessful: false
            };

            return isEqual(state, newState) ? state : newState;

        case EDIT_USER_SUCCESS:
            newState = {
                ...state,
                isEditUserLoading: false,
                isEditUserSuccessful: true
            };

            return isEqual(state, newState) ? state : newState;

        case DELETE_USER_FAILURE:
            newState = {
                ...state,
                deleteUserError: action.error,
                isDeleteUserLoading: false
            };

            return isEqual(state, newState) ? state : newState;

        case DELETE_USER_LOAD:
            newState = {
                ...state,
                deleteUserError: null,
                isDeleteUserLoading: true,
                isDeleteUserSuccessful: false
            };

            return isEqual(state, newState) ? state : newState;

        case DELETE_USER_SUCCESS:
            newState = {
                ...state,
                isDeleteUserLoading: false,
                isDeleteUserSuccessful: true
            };

            return isEqual(state, newState) ? state : newState;

        case USERS_LOAD:
            newState = {
                ...state,
                usersError: null,
                isUsersLoading: true
            };

            return isEqual(state, newState) ? state : newState;

        case USERS_SUCCESS:
            newState = {
                ...state,
                count: action.count,
                pages: action.pages,
                users: action.users,
                isUsersLoading: false
            };

            return isEqual(state, newState) ? state : newState;

        case USERS_FAILURE:
            newState = {
                ...state,
                count: 0,
                isUsersLoading: false,
                pages: 0,
                users: [],
                usersError: action.error
            };

            return isEqual(state, newState) ? state : newState;

        case ROLES_LOAD:
            newState = {
                ...state,
                rolesError: null,
                isRolesLoading: true
            };

            return isEqual(state, newState) ? state : newState;

        case ROLES_SUCCESS:
            newState = {
                ...state,
                roles: action.roles,
                isRolesLoading: false
            };

            return isEqual(state, newState) ? state : newState;

        case ROLES_FAILURE:
            newState = {
                ...state,
                isRolesLoading: false,
                roles: [],
                rolesError: action.error
            };

            return isEqual(state, newState) ? state : newState;

        default:
            return state;
    }
}

export const getUserManagementEntity = createSelector(
    (state) => state,
    (state) => getEntityByName(state, 'userManagement')
);

export const getCreatedUser = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'createdUser')
);

export const getIsCreateUserLoading = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'isCreateUserLoading')
);

export const getIsCreateUserSuccessful = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'isCreateUserSuccessful')
);

export const getCreateUserError = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'createUserError')
);

export const getCreateUserErrorMessage = createSelector(
    getCreateUserError,
    (error) => get(error, 'message')
);

export const getIsEditUserLoading = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'isEditUserLoading')
);

export const getIsEditUserSuccessful = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'isEditUserSuccessful')
);

export const getEditUserError = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'editUserError')
);

export const getEditUserErrorMessage = createSelector(
    getEditUserError,
    (error) => get(error, 'message')
);

export const getIsDeleteUserLoading = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'isDeleteUserLoading')
);

export const getIsDeleteUserSuccessful = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'isDeleteUserSuccessful')
);

export const getDeleteUserError = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'deleteUserError')
);

export const getDeleteUserErrorMessage = createSelector(
    getDeleteUserError,
    (error) => get(error, 'message')
);

export const getCount = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'count')
);

export const getPages = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'pages')
);

export const getUsers = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'users')
);

export const getIsUsersLoading = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'isUsersLoading')
);

export const getUsersError = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'usersError')
);

export const getUsersErrorMessage = createSelector(
    getUsersError,
    (error) => get(error, 'message')
);

export const getRoles = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'roles')
);

export const getIsRolesLoading = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'isRolesLoading')
);

export const getRolesError = createSelector(
    getUserManagementEntity,
    (entity) => get(entity, 'rolesError')
);

export const getRolesErrorMessage = createSelector(
    getRolesError,
    (error) => get(error, 'message')
);
